import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContentText from "@material-ui/core/DialogContentText";
import { KeyboardArrowDown, KeyboardArrowUp, Delete as DeleteIcon, Edit as EditIcon } from "@material-ui/icons";
import { Button, Collapse, makeStyles } from "@material-ui/core";
import { Box } from "@material-ui/core";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { find, round, uniqBy } from "lodash";
import { generateProductUrl, getProductDetailsByProductType, getImageUrl } from "utils";
import { buildImageSlides, getFirstSlideUrl } from "utils/imageSlides";
import ImageCarouselDialog from "components/ImageCarouselDialog";
import { useSnackbar } from "notistack";
import { updateSubscriptionProduct } from "redux/action/customer.action";
import { getCustomer } from "redux/action/customer.action";
import { removeItem, removeBoxItem } from "redux/action/order.action";
import { getOrder } from "redux/action/order.action";
import ItemAddDialog from "../ItemAddOrEdit";

const useStyles = makeStyles(theme => ({
  thumbnail: {
    width: "60px",
    height: "60px",
    objectFit: "cover",
    borderRadius: "4px",
    cursor: "pointer",
    border: "1px solid #ddd"
  },
  imageModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative"
  },
  imageModalClose: {
    position: "absolute",
    right: 8,
    top: 8,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    zIndex: 1
  },
  subItemImage: {
    width: "50px",
    height: "50px",
    objectFit: "cover",
    borderRadius: "4px",
    marginRight: "8px",
    border: "1px solid #ddd"
  },
  strikethrough: {
    textDecoration: "line-through",
    color: "#999"
  },
  sizeContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  }
}));

export default function ProductLists(props) {
  const { row, fromScreen, userPriceVersion, allowEditProduct, showIsReturnedColumn = false, showItemDeleteColumn = false, showItemEditColumn = false } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const CUSTOMER_STATE = useSelector(state => state.customer);
  const ORDER_STATE = useSelector(state => state.order);
  const [open, setOpen] = useState(false);

  const [currentProductId, setCurrentProductId] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [editItem, setEditItem] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedSlides, setSelectedSlides] = useState([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const _pvProduct = (ORDER_STATE?.products && userPriceVersion) ? find(ORDER_STATE.products, product => product.price_version === userPriceVersion) : null;
  const userProductTypes = _pvProduct ? Object.keys(_pvProduct).slice(1) : undefined;

  const defectLabel = t("productLists.defect", "Defect");

  const handleImageClick = item => {
    const slides = buildImageSlides(item, defectLabel);
    if (slides.length) {
      setSelectedSlides(slides);
      setSlideIndex(0);
      setImageModalOpen(true);
    }
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedSlides([]);
    setSlideIndex(0);
  };

  const handleUpdateSubscriptionProductSuccess = () => {
    dispatch(getCustomer({ id: CUSTOMER_STATE.customer._id }));
    setCurrentProductId(null);
    setSelectedProductType(null);
    enqueueSnackbar(t("productLists.productUpdated"), { variant: "success" });
  };
  const handleUpdateSubscriptionProductFailure = ({ error }) => {
    enqueueSnackbar(error, { variant: "error" });
  };

  const handleUpdateProduct = () => {
    dispatch(
      updateSubscriptionProduct(
        { userId: CUSTOMER_STATE.customer._id, currentProductId, selectedProductType },
        handleUpdateSubscriptionProductSuccess,
        handleUpdateSubscriptionProductFailure
      )
    );
  };

  const handleCloseDialog = () => {
    setCurrentProductId(null);
    setSelectedProductType(null);
  };

  const handleDeleteItem = (itemId, isBoxProduct) => {
    const handleRemoveItemSuccess = () => {
      dispatch(getOrder({ id: ORDER_STATE.order._id, type: "purchase-order" }));
      enqueueSnackbar(t("productLists.itemRemoved"), { variant: "success" });
    };
    const handleRemoveItemFailure = error => {
      enqueueSnackbar(error, { variant: "error" });
    };

    const orderId = ORDER_STATE?.order?._id || row?.order_id?._id || params?.id;
    if (isBoxProduct && orderId) {
      dispatch(removeBoxItem({ orderId, itemId, handleRemoveItemSuccess, handleRemoveItemFailure }));
    } else {
      dispatch(removeItem({ orderType: "purchase-order", itemId, handleRemoveItemSuccess, handleRemoveItemFailure }));
    }
  };

  const handleCloseItemDialog = () => {
    setEditItem(null);
  };
  const handleCloseSuccessItemDialog = () => {
    const orderId = ORDER_STATE?.order?._id || row?.order_id?._id || params?.id;
    setEditItem(null);
    if (orderId) dispatch(getOrder({ id: orderId, type: "purchase-order" }));
    enqueueSnackbar(t("productLists.itemEdited"), { variant: "success" });
  };

  return (
    <React.Fragment>
      {editItem && (
        <ItemAddDialog
          editItem={editItem}
          handleCloseItemDialog={handleCloseItemDialog}
          handleCloseSuccessItemDialog={handleCloseSuccessItemDialog}
          orderId={ORDER_STATE.order._id}
          orderStatus={ORDER_STATE.order?.status}
        />
      )}
      {allowEditProduct && currentProductId && (
        <div>
          <Dialog open={currentProductId} onClose={handleCloseDialog}>
            <DialogContent>
              <DialogContentText color="black">
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableBody>
                    {userProductTypes?.map(productType => (
                      <TableRow key={row.name} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                        <TableCell scope="row">
                          <img style={{ height: "70px" }} alt="product" src={generateProductUrl(productType)} />
                        </TableCell>
                        <TableCell align="right">
                          {getProductDetailsByProductType(ORDER_STATE.allPriceVersionProducts[CUSTOMER_STATE.customer.price_version], productType).name}
                        </TableCell>
                        <TableCell align="right">
                          {selectedProductType === productType ? (
                            <Button variant="contained" color="primary">
                              {t("productLists.selected")}
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              color="primary"
                              disabled={productType === "box"}
                              onClick={() => {
                                setSelectedProductType(productType);
                              }}
                            >
                              {t("productLists.change")}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" disabled={CUSTOMER_STATE.updateSubscriptionProductLoading} onClick={handleCloseDialog}>
                {t("productLists.cancel")}
              </Button>
              <Button variant="contained" color="primary" onClick={handleUpdateProduct} disabled={CUSTOMER_STATE.updateSubscriptionProductLoading}>
                {CUSTOMER_STATE.updateSubscriptionProductLoading ? t("common.loading") : t("productLists.update")}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell size="medium">
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>

        <TableCell size="medium" component="th" scope="row">
          {row._id}
        </TableCell>
        {fromScreen ? (
          <TableCell align="center" size="medium">
            <Link to={`/admin/orders/${row.order_id?._id}`}>
              <div style={{ fontWeight: "400" }}>{row.order_id?._id}</div>
            </Link>
          </TableCell>
        ) : null}
        <TableCell align="center" size="medium">
          {row.product_type_id?.name}
        </TableCell>
        {showIsReturnedColumn && (
          <TableCell align="center" size="medium">
            {row.return ? t("productLists.yes") : t("productLists.no")}
          </TableCell>
        )}
        <TableCell align="center" size="medium">
          {uniqBy(row.items || [], '_id').length}
        </TableCell>
        <TableCell align="center" size="medium">
          <div className={classes.sizeContainer}>
            {(() => {
              const uniqueItems = uniqBy(row.items || [], '_id');
              const totalOriginalSize = uniqueItems.reduce((sum, item) => sum + (item.size || item.original_size_m2 || 0), 0);
              const totalMeasuredSize = uniqueItems.reduce((sum, item) => sum + (item.measured_size_m2 || 0), 0);
              const hasSizeDifference = totalOriginalSize > 0 && totalMeasuredSize > 0 && Math.abs(totalOriginalSize - totalMeasuredSize) > 0.01;

              if (totalOriginalSize > 0 || totalMeasuredSize > 0) {
                return (
                  <>
                    {totalOriginalSize > 0 ? (
                      <span className={hasSizeDifference ? classes.strikethrough : ""}>
                        {round(totalOriginalSize, 1)} m²
                      </span>
                    ) : null}
                    {totalMeasuredSize > 0 ? (
                      <span style={{ fontWeight: hasSizeDifference ? "600" : "normal" }}>
                        {round(totalMeasuredSize, 1)} m² {hasSizeDifference && "(measured)"}
                      </span>
                    ) : null}
                  </>
                );
              }
              return "-";
            })()}
          </div>
        </TableCell>
        {allowEditProduct && (
          <TableCell align="center" size="medium">
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              disabled={row.product_type_id?.type === "box" || row.return}
              onClick={() => {
                setCurrentProductId(row._id);
                setSelectedProductType(row.product_type_id?.type);
              }}
            >
              {t("productLists.editProduct")}
            </Button>
          </TableCell>
        )}
      </TableRow>
      {uniqBy(row.items || [], '_id').length > 0 && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0, padding: "0px 74px" }} colSpan={10}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1, padding: "50px" }}>
                <Table size="small" aria-label="a dense table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("productLists.id")}</TableCell>
                      <TableCell>{t("productLists.contentName")}</TableCell>
                      <TableCell align="center">{t("productLists.quantity")}</TableCell>
                      <TableCell align="center">{t("productLists.size", "Size (m²)")}</TableCell>
                      <TableCell align="center">{t("productLists.picture", "Picture")}</TableCell>
                      <TableCell align="center">{t("productLists.barcodeIds")}</TableCell>
                      <TableCell align="center">{t("productLists.defects", "Defects")}</TableCell>
                      <TableCell align="center">{t("productLists.returnId")}</TableCell>
                      {(showItemDeleteColumn || showItemEditColumn) && <TableCell align="center">{t("productLists.action")}</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uniqBy(row.items || [], '_id').map(item => {
                      const originalSize = item.size || item.original_size_m2;
                      const measuredSize = item.measured_size_m2;
                      const hasSizeDifference = originalSize && measuredSize && Math.abs(originalSize - measuredSize) > 0.01;
                      const firstImageUrl = getFirstSlideUrl(item, defectLabel);
                      const defects = item?.defects || [];
                      const firstDefectImage = defects.find(defect => defect.defect_image_url)?.defect_image_url;

                      return (
                        <TableRow key={item._id}>
                          <TableCell component="th" scope="row">
                            {item._id}
                          </TableCell>
                          <TableCell>{item.content_name}</TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="center">
                            <div className={classes.sizeContainer}>
                              {(originalSize != null && originalSize !== "") ? (
                                <span className={hasSizeDifference ? classes.strikethrough : ""}>
                                  {round(originalSize, 1)} m²
                                </span>
                              ) : null}
                              {(measuredSize != null && measuredSize !== "") ? (
                                <span style={{ fontWeight: hasSizeDifference ? "600" : "normal" }}>
                                  {round(measuredSize, 1)} m² {hasSizeDifference && "(measured)"}
                                </span>
                              ) : null}
                              {(originalSize == null || originalSize === "") && (measuredSize == null || measuredSize === "") && "-"}
                            </div>
                          </TableCell>
                          <TableCell align="center">
                            {firstImageUrl ? (
                              <img
                                src={firstImageUrl}
                                alt="Item"
                                className={classes.thumbnail}
                                onClick={() => handleImageClick(item)}
                                onError={e => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {item?.sub_items?.filter(sub_item => sub_item.barcode_id).length > 0
                              ? uniqBy(item.sub_items.filter(sub_item => sub_item.barcode_id), '_id')
                                  .map((sub_item, index) => (
                                    <div key={sub_item._id || index} style={{ marginBottom: "4px" }}>
                                      <a href={sub_item.barcode_image_url} target="_blank" rel="noopener noreferrer">
                                        {sub_item.barcode_id}
                                      </a>
                                    </div>
                                  ))
                              : "-"}
                          </TableCell>
                          <TableCell align="center">
                            {defects.length > 0 ? (
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                                {firstDefectImage && (
                                  <img
                                    src={getImageUrl(firstDefectImage)}
                                    alt="Defect"
                                    className={classes.thumbnail}
                                    onClick={() => handleImageClick(item)}
                                    onError={e => {
                                      e.target.style.display = "none";
                                    }}
                                    style={{ border: "2px solid #ff9800" }}
                                  />
                                )}
                                <span style={{ fontSize: "0.85em", color: "#ff9800", fontWeight: "500" }}>
                                  {defects.length} {defects.length === 1 ? t("productLists.defect", "defect") : t("productLists.defects", "defects")}
                                </span>
                              </div>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell align="center">{item.return_id ? item.return_id : "-"}</TableCell>
                            {(showItemDeleteColumn || showItemEditColumn) && (
                            <TableCell align="center" size="medium">
                              {showItemDeleteColumn && (
                                <IconButton aria-label="expand row" size="small" onClick={() => handleDeleteItem(item._id, row.product_type_id?.type === "box")}>
                                  <DeleteIcon />
                                </IconButton>
                              )}
                              {showItemEditColumn && !item?.sub_items?.some(si => si?.barcode_id) && (
                                <IconButton aria-label="expand row" size="small" onClick={() => setEditItem(item)}>
                                  <EditIcon />
                                </IconButton>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
      <ImageCarouselDialog
        open={imageModalOpen}
        onClose={handleCloseImageModal}
        slides={selectedSlides}
        initialIndex={slideIndex}
      />
    </React.Fragment>
  );
}
